import React, { useEffect, useState } from "react";
import { studioApi, NIGGLES } from "../../lib/studioApi";
import { Plus, X } from "lucide-react";
import { Btn, LabeledInput } from "../plan/shared";

const area = "bg-[#0b0c10] border border-zinc-800 focus:border-[#BEFF38] rounded-lg px-3 py-2.5 text-white text-sm outline-none w-full";
const mini = "bg-[#0b0c10] border border-zinc-800 focus:border-[#BEFF38] rounded-md px-2.5 py-2 text-white text-xs outline-none w-full";
const BLANK = { label: "", title: "", detail: "", why: "" };

const SlideFields = ({ slides, onChange, max = 5 }) => {
  const set = (i, k, v) => onChange(slides.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div className="grid gap-2" data-testid="editor-slides">
      <span className="text-zinc-300 font-medium text-sm">{max === 3 ? "Fixes" : "Info slides"} ({slides.length}/{max})</span>
      {slides.map((s, i) => (
        <div key={i} className="rounded-lg border border-zinc-800 p-2.5 grid gap-1.5 relative">
          <button onClick={() => onChange(slides.filter((_, j) => j !== i))} aria-label="Remove slide" data-testid={`editor-slide-remove-${i}`} className="absolute top-2 right-2 text-zinc-500 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
          <span className="text-[10px] font-mono text-[#BEFF38]">SLIDE {i + 2}</span>
          <div className="grid grid-cols-[110px_1fr] gap-1.5">
            <input className={mini} placeholder="Label" value={s.label} onChange={(e) => set(i, "label", e.target.value)} data-testid={`editor-slide-label-${i}`} />
            <input className={mini} placeholder="Title" value={s.title} onChange={(e) => set(i, "title", e.target.value)} data-testid={`editor-slide-title-${i}`} />
          </div>
          <input className={mini} placeholder="Detail e.g. 3 x 8 · 3s down · 2x/week" value={s.detail} onChange={(e) => set(i, "detail", e.target.value)} data-testid={`editor-slide-detail-${i}`} />
          <input className={mini} placeholder="Why it works" value={s.why} onChange={(e) => set(i, "why", e.target.value)} data-testid={`editor-slide-why-${i}`} />
        </div>
      ))}
      {slides.length < max && (
        <button onClick={() => onChange([...slides, { ...BLANK }])} data-testid="editor-slide-add" className="text-xs text-[#BEFF38] flex items-center gap-1 self-start hover:underline">
          <Plus className="w-3.5 h-3.5" /> Add slide
        </button>
      )}
    </div>
  );
};

const ScreenFields = ({ value, onChange }) => {
  const [opts, setOpts] = useState([]);
  useEffect(() => { studioApi.screens().then(setOpts); }, []);
  const v = value || { key: "", title: "", caption: "" };
  const set = (k) => (e) => onChange({ ...v, [k]: e.target.value });
  return (
    <div className="rounded-lg border border-[#BEFF38]/30 p-2.5 grid gap-1.5" data-testid="editor-app-screen">
      <span className="text-[10px] font-mono text-[#BEFF38]">REAL APP SCREEN SLIDE (optional)</span>
      <select className={mini} value={v.key} onChange={set("key")} data-testid="editor-screen-key">
        <option value="">No app screen</option>
        {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      {v.key && (
        <>
          <input className={mini} placeholder="Screen title" value={v.title} onChange={set("title")} data-testid="editor-screen-title" />
          <input className={mini} placeholder="What you see / do here" value={v.caption} onChange={set("caption")} data-testid="editor-screen-caption" />
        </>
      )}
    </div>
  );
};

export const PostEditor = ({ post, onSave, onCancel }) => {
  const [f, setF] = useState({
    hook: post.hook,
    subhead: post.subhead,
    caption: post.caption,
    cta: post.cta,
    soft_line: post.soft_line || "",
    hashtags: (post.hashtags || []).join(" "),
    story_bullets: (post.story_bullets || []).join("\n"),
    scheduled_for: post.scheduled_for || "",
    niggle_region: post.niggle_region || post.niggle || "",
    question: post.question || "",
    reply_keyword: post.reply_keyword || "",
  });
  const niggle = post.kind === "niggle";
  const [slides, setSlides] = useState(post.slides || []);
  const [screen, setScreen] = useState(post.app_screen || null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await onSave({
        ...f,
        slides: slides.filter((s) => s.title.trim()),
        app_screen: screen?.key ? screen : {},
        hashtags: f.hashtags.split(/[\s,]+/).filter(Boolean),
        story_bullets: f.story_bullets.split("\n").map((b) => b.trim()).filter(Boolean).slice(0, 3),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-3" data-testid={`post-editor-${post.id}`}>
      <LabeledInput label={niggle ? "Intro hook (wrap *words* to highlight)" : "Cover headline (wrap *words* to highlight)"} value={f.hook} onChange={set("hook")} data-testid="editor-hook" />
      <label className="flex flex-col gap-1.5 text-sm">
        <span className="text-zinc-300 font-medium">{niggle ? "Niggle" : "Normal vs adapted slide (real Safety Pass swaps)"}</span>
        <select className={mini} value={f.niggle_region} onChange={set("niggle_region")} data-testid="editor-niggle-region">
          {!niggle && <option value="">No comparison slide</option>}
          {NIGGLES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      {niggle ? (
        <>
          <LabeledInput label="Reply-frame question" value={f.question} onChange={set("question")} data-testid="editor-question" />
          <LabeledInput label="Reply keyword" value={f.reply_keyword} onChange={set("reply_keyword")} data-testid="editor-reply-keyword" />
          <SlideFields slides={slides} onChange={setSlides} max={3} />
        </>
      ) : (
        <>
          <LabeledInput label="Cover subline" value={f.subhead} onChange={set("subhead")} data-testid="editor-subhead" />
          <ScreenFields value={screen} onChange={setScreen} />
          <SlideFields slides={slides} onChange={setSlides} />
          <LabeledInput label="Final slide line (soft app mention)" value={f.soft_line} onChange={set("soft_line")} data-testid="editor-soft-line" />
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-zinc-300 font-medium">Story bullets (one per line, max 3)</span>
            <textarea rows={3} value={f.story_bullets} onChange={set("story_bullets")} className={area} data-testid="editor-story-bullets" />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-zinc-300 font-medium">Caption</span>
            <textarea rows={8} value={f.caption} onChange={set("caption")} className={area} data-testid="editor-caption" />
          </label>
          <LabeledInput label="Call to action" value={f.cta} onChange={set("cta")} data-testid="editor-cta" />
          <LabeledInput label="Hashtags (space separated)" value={f.hashtags} onChange={set("hashtags")} data-testid="editor-hashtags" />
        </>
      )}
      <LabeledInput label="Scheduled for" type="date" value={f.scheduled_for} onChange={set("scheduled_for")} data-testid="editor-date" />
      <div className="flex gap-2">
        <Btn onClick={save} disabled={busy} data-testid="editor-save-btn">{busy ? "Re-rendering…" : "Save & re-render"}</Btn>
        <Btn variant="ghost" onClick={onCancel} data-testid="editor-cancel-btn">Cancel</Btn>
      </div>
    </div>
  );
};
